import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

// Phase 1: Trigger + Anzeige der automatisierten Audit-Engine (aw-audit-engine).
// Faehrt einen Scan gegen einen Repo-Pfad, materialisiert die Findings am Projekt
// (siehe backend/routes/audit_engine.py) und zeigt Score + Gate-Reasons.
export default function AuditEnginePanel({ projectId }) {
  const [workspacePath, setWorkspacePath] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState("");

  const loadRuns = useCallback(() => {
    if (!projectId) return;
    api
      .get("/audit-engine/runs", { params: { project_id: projectId } })
      .then((r) => setRuns(r.data))
      .catch(() => setRuns([]));
  }, [projectId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const runScan = async () => {
    if (!workspacePath.trim() || !projectId) return;
    setError("");
    setResult(null);
    setRunning(true);
    try {
      const r = await api.post("/audit-engine/scan", {
        workspace_path: workspacePath.trim(),
        project_id: projectId,
      });
      setResult(r.data);
      loadRuns();
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
    } finally {
      setRunning(false);
    }
  };

  const rep = result?.report;
  const integ = result?.studio_integration;

  return (
    <div className="mb-8 border border-neutral-200 rounded-sm p-5 bg-neutral-50/50">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-neutral-900">Audit-Engine — automatisierter Scan</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">aw-audit-engine v1</span>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Scannt einen Repo-Pfad (semgrep / trivy / Heuristik) und legt die Findings am Projekt an.
        Ohne semgrep/trivy auf dem Host werden die Domains ehrlich als „uncovered" gemeldet.
      </p>

      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={workspacePath}
          onChange={(e) => setWorkspacePath(e.target.value)}
          placeholder="/absoluter/pfad/zum/repo"
          className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-sm font-mono"
          disabled={running}
        />
        <button
          onClick={runScan}
          disabled={running || !projectId || !workspacePath.trim()}
          className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
          style={{ background: "#183153" }}
        >
          {running ? "Scan läuft…" : "Scan starten"}
        </button>
      </div>

      {error && <div className="text-xs text-red-600 mt-2">Fehler: {error}</div>}

      {rep && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Score" value={rep.raw_technical_score} />
          <Stat label="Engine-Status" value={rep.engine_status} />
          <Stat label="Findings angelegt" value={integ?.materialized ?? "—"} />
          <Stat
            label="Blockierende Gates"
            value={(rep.gate_reasons || []).filter((g) => g.blocking).map((g) => g.code).join(", ") || "keine"}
          />
        </div>
      )}

      {runs.length > 0 && (
        <div className="mt-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Bisherige Läufe</div>
          <div className="space-y-1">
            {runs.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-xs text-neutral-600 font-mono">
                <span className="text-neutral-400">{(r.created_at || "").slice(0, 19).replace("T", " ")}</span>
                <span>Score {r.raw_technical_score}</span>
                <span>· {r.engine_status}</span>
                <span>· {r.findings_count} Findings</span>
                {(r.gate_reasons || []).some((g) => g.blocking) && (
                  <span className="text-red-600">· Gate</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-neutral-200 rounded-sm px-3 py-2 bg-white">
      <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-400">{label}</div>
      <div className="text-neutral-900 mt-0.5 break-words">{String(value)}</div>
    </div>
  );
}
